import React from 'react';
import { motion } from 'framer-motion';
import { fadeUp } from '../../utils/motion';
export type Topic = {
  title: string;
  paragraphs: string[];
  /** Earlier anchors that should still land on this topic. */
  aliasIds?: string[];
};
type TopicIndexProps = {
  topics: Topic[];
  indexLabel: string;
  /** Optional visible heading above the topics. */
  heading?: string;
  headingNote?: string;
};
export function toTopicId(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
export function TopicIndex({
  topics,
  indexLabel,
  heading,
  headingNote
}: TopicIndexProps) {
  const scrollTo = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section className="bg-white py-20 lg:py-28" data-id="element-1434" data-mp="25zhj19zohufo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1435" data-mp="212r3cnrsd0us">
        {heading && <motion.div {...fadeUp} className="mb-12 max-w-3xl" data-id="element-1436" data-mp="zd7m4m5yf61i">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-1437" data-mp="zav8umb55jus">{heading}</h2>
            {headingNote && <p className="mt-4 text-lg text-ink-muted leading-relaxed" data-id="element-1438" data-mp="184rix2r175tu">{headingNote}</p>}
          </motion.div>}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-1439" data-mp="1d3b4h21pqflo">
          <nav aria-label={indexLabel} className="hidden lg:block lg:col-span-4" data-id="element-1440" data-mp="gell17aftexl">
            <div className="sticky top-28" data-id="element-1441" data-mp="kfoy5m13owv4">
              <p className="text-sm font-medium text-ink-muted mb-4" data-id="element-1442" data-mp="kggnwg80o0dh">{indexLabel}</p>
              <ul className="border-l border-line" data-id="element-1443" data-mp="pdqkcfttpcat">
                {topics.map(topic => <li key={topic.title} data-id="element-1444" data-mp="ue4qttfs0e6q">
                    <a href={`#${toTopicId(topic.title)}`} onClick={scrollTo(toTopicId(topic.title))} className="block -ml-px border-l-2 border-transparent hover:border-navy pl-4 py-1.5 text-sm text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-1445" data-mp="ubs1uwblp3k6">
                      {topic.title}
                    </a>
                  </li>)}
              </ul>
            </div>
          </nav>

          <div className="lg:col-span-8" data-id="element-1446" data-mp="1q2aic9ve8juq">
            {topics.map(topic => <motion.article key={topic.title} id={toTopicId(topic.title)} {...fadeUp} className="relative scroll-mt-28 border-t border-line py-12 first:border-t-0 first:pt-0" data-id="element-1447" data-mp="1l4iimtoibtfz">
                {topic.aliasIds?.map(alias => <span key={alias} id={alias} aria-hidden="true" className="absolute -top-28 block" data-id="element-1448" data-mp="175istip9d8i8" />)}
                {React.createElement(heading ? 'h3' : 'h2', {
              className: 'font-heading font-bold text-2xl lg:text-3xl text-ink-dark tracking-tight mb-5'
            }, topic.title)}
                <div className="space-y-4" data-id="element-1449" data-mp="2lbvwx67rg16o">
                  {topic.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-1450" data-mp="2q3xjbkyn78af">
                      {paragraph}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </div>
      </div>
    </section>;
}